import { api } from './api';
import { MotiviewGroupConfig } from '../../typings/target_group';

export interface GroupAPI {
    id: string;
    assessment_id: string;
    company_id: string;
    name: string;
    description?: string;
    created_at: string;
    updated_at: string;
    expiration_date: string;
    baseline?: GroupBaselineObject;
    config?: GroupConfigObject;
    integrations?: GroupIntegrations;
    type?: GroupType;
    relations?: GroupRelationsObject;
    participants_count: number;
    motiview_config?: MotiviewGroupConfig;
}

export interface Group {
    id: string;
    assessment_id: string;
    company_id: string;
    name: string;
    description?: string;
    created_at: string;
    updated_at: string;
    expiration_date: string;
    baseline?: GroupBaselineObject;
    config?: GroupConfigObject;
    integrations?: GroupIntegrations;
    type: GroupType;
    relations?: GroupRelationsObject;
    participants_count: number;
    motiview_config?: MotiviewGroupConfig;
}

export interface GroupConfigObject {
    has_opt_out_questionnaire: boolean;
    is_public?: boolean;
    verify_emails?: boolean;
    auto_send_reports?: boolean;
    allowed_email_domains?: string;
    scan_completion_notification?: boolean;
    scan_completion_notification_emails?: string;
    report_generation_notification?: boolean;
    report_generation_notification_emails?: string;
    hide_company_baseline_in_pdf?: boolean;
}

export interface GroupBaselineObject {
    culture_fit?: string;
    cognitive_abilities?: string;
}

export type GroupType = 'INTERNAL' | 'CANDIDATE' | 'MOTIVIEW';

export type GroupRelationsType = 'single' | 'base' | 'linked';

export interface GroupRelationsObject {
    type: GroupRelationsType;
    before?: string;
}

export interface GroupIntegrations {
    send_results: boolean;
    salesforce?: {
        is_active: boolean;
    };
    jazzhr?: {
        is_active: boolean;
        api_key: string;
    };
    afas?: {
        is_active: boolean;
        endpoint: string;
        api_key: string;
    };
    v2?: {
        is_active: boolean;
        send_results: boolean;
        webhook?: string;
        report_webhook?: string;
        api_key: string;
    };
}

export type WorkPreferenceBaselineData = {
    market: number;
    adhocracy: number;
    hierarchy: number;
    family: number;
}[];

export const groups = api.injectEndpoints({
    endpoints: (builder) => ({
        getGroup: builder.query<
            Group,
            { companyIdentifier: string; groupId: string }
        >({
            query: ({ companyIdentifier, groupId }) =>
                `companies/${companyIdentifier}/groups/${groupId}`,
            providesTags: (result, error, { groupId }) => [
                { type: 'Group', groupId },
            ],
            transformResponse: (response: GroupAPI) => ({
                ...response,
                type: response.type ?? 'CANDIDATE',
            }),
        }),

        getGroupWorkPreferencesBaseline: builder.query<
            WorkPreferenceBaselineData,
            { companyIdentifier: string; groupId: string }
        >({
            query: ({ companyIdentifier, groupId }) =>
                `companies/${companyIdentifier}/groups/${groupId}/work-preference-baseline`,
            providesTags: (result, error, { groupId }) => [
                { type: 'GroupWorkPreferencesBaseline', groupId },
            ],
        }),

        updateGroupConfig: builder.mutation<
            Group,
            {
                companyIdentifier: string;
                groupId: string;
                groupConfig: {
                    name?: string;
                    isPublic?: boolean;
                    verifyEmails?: boolean;
                    autoSendReports?: boolean;
                    description?: string;
                    allowedEmailDomains?: string;
                    scanCompletionNotification?: boolean;
                    scanCompletionNotificationEmails?: string;
                    reportGenerationNotification?: boolean;
                    reportGenerationNotificationEmails?: string;
                    hideCompanyBaselineInPdf?: boolean;
                    allowMotiviewRegistration?: boolean;
                    isWebsiteDemo?: boolean;
                    allowRepeatMotiviewScan?: boolean;
                    showMotiviewDetailsFormBeforeResult?: boolean;
                    showMotiviewDetailsFormAfterResult?: boolean;
                    allowMeetingAboveAverage?: boolean;
                    allowMeetingAverage?: boolean;
                    allowMeetingBelowAverage?: boolean;
                    meetingUrls?: string[];
                    showCompanyLogo?: boolean;
                    sendPdfReport?: boolean;
                };
            }
        >({
            query: ({ companyIdentifier, groupId, groupConfig }) => ({
                url: `companies/${companyIdentifier}/groups/${groupId}/config`,
                method: 'PUT',
                body: {
                    name: groupConfig.name,
                    is_public: groupConfig.isPublic,
                    verify_emails: groupConfig.verifyEmails,
                    auto_send_reports: groupConfig.autoSendReports,
                    description: groupConfig.description,
                    allowed_email_domains: groupConfig.allowedEmailDomains,
                    scan_completion_notification:
                        groupConfig.scanCompletionNotification,
                    scan_completion_notification_emails:
                        groupConfig.scanCompletionNotificationEmails,
                    report_generation_notification:
                        groupConfig.reportGenerationNotification,
                    report_generation_notification_emails:
                        groupConfig.reportGenerationNotificationEmails,
                    hide_company_baseline_in_pdf:
                        groupConfig.hideCompanyBaselineInPdf,
                    allow_motiview_registration:
                        groupConfig.allowMotiviewRegistration,
                    is_website_demo: groupConfig.isWebsiteDemo,
                    allow_repeat_motiview_scan:
                        groupConfig.allowRepeatMotiviewScan,
                    show_motiview_details_form_before_result:
                        groupConfig.showMotiviewDetailsFormBeforeResult,
                    show_motiview_details_form_after_result:
                        groupConfig.showMotiviewDetailsFormAfterResult,
                    allow_meeting_above_average:
                        groupConfig.allowMeetingAboveAverage,
                    allow_meeting_average: groupConfig.allowMeetingAverage,
                    allow_meeting_below_average:
                        groupConfig.allowMeetingBelowAverage,
                    meeting_urls: groupConfig.meetingUrls,
                    show_company_logo: groupConfig.showCompanyLogo,
                    send_pdf_report: groupConfig.sendPdfReport,
                },
            }),
            invalidatesTags: (result, error, { groupId }) => [
                {
                    type: 'Group',
                    groupId,
                },
            ],
            transformResponse: (response: GroupAPI) => ({
                ...response,
                type: response.type ?? 'CANDIDATE',
            }),
        }),

        deleteGroup: builder.mutation<
            'success',
            {
                companyIdentifier: string;
                groupId: string;
            }
        >({
            query: ({ companyIdentifier, groupId }) => ({
                url: `companies/${companyIdentifier}/groups/${groupId}`,
                method: 'DELETE',
            }),
            invalidatesTags: (result, error, { companyIdentifier }) => [
                {
                    type: 'Company',
                    id: companyIdentifier,
                },
            ],
        }),
    }),
});

export const {
    useGetGroupQuery,
    useLazyGetGroupQuery,
    useGetGroupWorkPreferencesBaselineQuery,
    useLazyGetGroupWorkPreferencesBaselineQuery,
    useUpdateGroupConfigMutation,
    useDeleteGroupMutation,
    util: { getRunningQueriesThunk },
} = groups;

// Export the API slice for use in SSR (if needed)
export const { getGroup } = groups.endpoints;
